<script lang="ts" setup>
import { NuxtLink } from '#components';
import { Heading, Text } from '~ds/components/ui/typography';

/**
 * One of the student's running numbers (clarity, coins) as a pill in the signed-in navigation:
 * a picture, the number and what it counts. `md` sits beside the desktop links, `lg` in the menu.
 * `selected` is for the pill whose page is being looked at: outlined, its label in ink, and the
 * current page.
 */
interface Props {
  value: string;
  label: string;
  to?: string;
  tone?: 'white' | 'lime';
  size?: 'md' | 'lg';
  selected?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  to: undefined,
  tone: 'white',
  size: 'md',
  selected: false,
});

// The selected outline is an inset ring, which takes no space, so selecting a pill never moves it.
const rootClasses = computed(() => twMerge(cva('inline-flex shrink-0 items-center rounded-full pl-2 pr-4 transition-opacity hover:opacity-80', {
  variants: {
    selected: {
      true: 'ring-1 ring-inset ring-foreground',
      false: '',
    },
    tone: {
      white: 'bg-card',
      lime: 'bg-secondary',
    },
    size: {
      md: 'h-12.5 gap-2.5',
      lg: 'gap-3.5 py-2',
    },
  },
})({ tone: props.tone, size: props.size, selected: props.selected })));

// The label is in ink at medium weight when selected; `after` reserves that width either way, so the
// pill does not resize as the weight changes.
const labelClasses = computed(() => twMerge(cva('after:invisible after:block after:h-0 after:overflow-hidden after:font-medium after:content-[attr(data-text)]', {
  variants: {
    selected: {
      true: 'text-foreground',
      false: 'text-muted-foreground',
    },
  },
})({ selected: props.selected })));

const mediaClasses = computed(() => (props.size === 'lg' ? 'size-13' : 'size-8.5'));
</script>

<template>
  <component
    :is="to ? NuxtLink : 'span'"
    :to="to"
    :class="rootClasses"
    :aria-current="selected ? 'page' : undefined"
  >
    <span
      class="inline-flex shrink-0 items-center justify-center"
      :class="[mediaClasses]"
      aria-hidden="true"
    >
      <slot name="media" />
    </span>
    <span class="flex flex-col items-start whitespace-nowrap">
      <Heading
        v-if="size === 'lg'"
        as="span"
        size="xl"
      >
        {{ value }}
      </Heading>
      <Text
        v-else
        as="span"
        size="lg"
        weight="semibold"
      >
        {{ value }}
      </Text>
      <Text
        as="span"
        :size="size === 'lg' ? 'sm' : 'xs'"
        :weight="selected ? 'medium' : 'normal'"
        :data-text="label"
        :class="labelClasses"
      >
        {{ label }}
      </Text>
    </span>
  </component>
</template>
